/**
 * Demandes de pièces en cours sur un dossier, vues de la review Skipcar.
 *
 * La route d'annulation existe depuis le 22/08/2026 mais n'était appelée nulle
 * part : le front ne connaissait que `fulfill`. Résultat, une demande qu'aucun
 * dépôt ne peut satisfaire (pièce finalement inutile, demande faite en double,
 * pièce transmise hors application) tenait le dossier en « Pièces à déposer »
 * indéfiniment, sans porte de sortie côté Skipcar. C'est ce qui a bloqué
 * ED-063-NC : la déclaration d'achat avait bien été faite, elle n'était juste
 * pas déposable.
 *
 * Le panneau ne s'affiche que s'il reste des demandes ouvertes : sur un dossier
 * sain, il n'ajoute rien à l'écran.
 */
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { makeAuthenticatedApiCall } from '../../apiHelper';

interface DocumentRequest {
  id: number;
  docType: string;
  label: string;
  reason: string | null;
  status: 'open' | 'fulfilled' | 'cancelled';
  createdAt: string;
}

interface DocumentRequestsResponse {
  success: boolean;
  data: DocumentRequest[];
}

interface OpenDocumentRequestsPanelProps {
  registrationId: number;
  /** Rejoué après une annulation : le statut du dossier a pu changer. */
  onChanged?: () => void;
}

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
};

export const OpenDocumentRequestsPanel = ({ registrationId, onChanged }: OpenDocumentRequestsPanelProps) => {
  const queryClient = useQueryClient();
  const [pendingCancel, setPendingCancel] = useState<DocumentRequest | null>(null);

  const { data, isLoading } = useQuery<DocumentRequestsResponse>({
    queryKey: ['cgDocumentRequests', registrationId],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<DocumentRequestsResponse>(
        'get',
        `/api/cg/${registrationId}/document-requests`,
      );
      return response.data;
    },
    enabled: Number.isInteger(registrationId) && registrationId > 0,
  });

  const cancelMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await makeAuthenticatedApiCall('post', `/api/cg/document-requests/${requestId}/cancel`, {});
      return response.data;
    },
    onSuccess: async () => {
      setPendingCancel(null);
      await queryClient.invalidateQueries({ queryKey: ['cgDocumentRequests', registrationId] });
      onChanged?.();
    },
  });

  const openRequests = (data?.data ?? []).filter((request) => request.status === 'open');

  if (isLoading || openRequests.length === 0) return null;

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Pièces demandées à l’agence
        </Typography>
        <Chip size="small" color="warning" label={openRequests.length} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Tant qu’une demande reste ouverte, le dossier attend côté agence. Annule celles qui n’ont plus lieu d’être.
      </Typography>

      {cancelMutation.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {(cancelMutation.error as any)?.response?.data?.error ?? 'Annulation impossible'}
        </Alert>
      )}

      <Stack spacing={1}>
        {openRequests.map((request) => (
          <Stack
            key={request.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.5 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {request.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {request.reason ? `${request.reason} · ` : ''}
                demandée le {formatDate(request.createdAt)}
              </Typography>
            </Box>
            <Button
              size="small"
              color="warning"
              startIcon={<CancelIcon />}
              onClick={() => setPendingCancel(request)}
              disabled={cancelMutation.isPending}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Annuler
            </Button>
          </Stack>
        ))}
      </Stack>

      {/* Confirmation : l'annulation de la dernière demande ouverte remet le
          dossier dans la file de review, ce n'est pas un geste anodin. */}
      <Dialog open={pendingCancel !== null} onClose={() => setPendingCancel(null)}>
        <DialogTitle>Annuler cette demande ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            « {pendingCancel?.label} » ne sera plus réclamée à l’agence.
            {openRequests.length === 1
              ? ' C’est la dernière demande ouverte : le dossier repartira en review.'
              : ` Il restera ${openRequests.length - 1} demande(s) ouverte(s).`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingCancel(null)} sx={{ textTransform: 'none' }}>
            Revenir
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => pendingCancel && cancelMutation.mutate(pendingCancel.id)}
            disabled={cancelMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            {cancelMutation.isPending ? <CircularProgress size={18} /> : 'Annuler la demande'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenDocumentRequestsPanel;
